/**
 * One shared answer per key for the project Home's gadgets (components/home/gadgets): two gadgets that show the same
 * data (the Traces map for Requests and API health, the Quality overview for Dependencies, Tests and Security, the
 * app configuration for Jobs, Configuration health and Open work…) read it once. The key names the project
 * (`quality:<projectId>`), so another project never sees it.
 *
 * - Read when a gadget that needs it is on the board (mounted) and the answer is older than `maxAgeMs`; one request at
 *   a time per key, whoever asks.
 * - Read again every `maxAgeMs` while someone shows it and the tab is visible (never faster than GADGET_REFRESH_MS, the
 *   release gate's pace); a hidden tab asks nothing.
 * - An error keeps the last good answer (`data`) and says so (`error`).
 * - `refreshGadgetData(projectId)`: the Home's Refresh reads again every key of that project someone shows.
 */
import { useCallback, useEffect, useState } from 'react';

export const GADGET_REFRESH_MS = 60000;
const EMPTY = Object.freeze({ data: null, error: null, loading: true, at: 0 });
const entries = new Map();

function entryOf(key) {
  if (!entries.has(key)) { entries.set(key, { value: EMPTY, inflight: null, listeners: new Set(), loader: null }); }
  return entries.get(key);
}

/** Reads `key` again with its loader (one call at a time); resolves to the new value. */
export function loadGadgetData(key, loader = null) {
  const entry = entryOf(key);
  if (loader) { entry.loader = loader; }
  if (entry.inflight) { return entry.inflight; }
  if (!entry.loader) { return Promise.resolve(entry.value); }
  entry.inflight = Promise.resolve()
    .then(() => entry.loader())
    .then((data) => ({ data, error: null, loading: false, at: Date.now() }))
    .catch((error) => ({ data: entry.value.data, error, loading: false, at: Date.now() }))
    .then((value) => {
      entry.value = value;
      entry.inflight = null;
      entry.listeners.forEach((listener) => listener(value));
      return value;
    });
  return entry.inflight;
}

/** Reads again every key of `projectId` someone shows now (the Home's Refresh). */
export function refreshGadgetData(projectId) {
  const suffix = `:${projectId}`;
  return Promise.allSettled([...entries.entries()]
    .filter(([key, entry]) => key.endsWith(suffix) && entry.listeners.size > 0)
    .map(([key]) => loadGadgetData(key)));
}

/** For tests: forget every answer. */
export function resetGadgetData() {
  entries.clear();
}

/**
 * `{ data, error, loading, refresh }` of `key`, read with `loader` (`() => Promise`). `key` null: nothing is read
 * (`loading` false, `data` null). `maxAgeMs`: how old an answer may be before it is read again (and the poll's pace).
 */
export default function useGadgetData(key, loader, { maxAgeMs = GADGET_REFRESH_MS } = {}) {
  const [value, setValue] = useState(() => (key ? entryOf(key).value : { ...EMPTY, loading: false }));
  const every = Math.max(GADGET_REFRESH_MS, maxAgeMs);

  useEffect(() => {
    if (!key) { setValue({ ...EMPTY, loading: false }); return undefined; }
    const entry = entryOf(key);
    entry.loader = loader;
    setValue(entry.value);
    entry.listeners.add(setValue);
    const stale = () => Date.now() - entry.value.at >= every;
    if (stale()) { loadGadgetData(key); }
    const visible = () => typeof document === 'undefined' || !document.hidden;
    const timer = setInterval(() => { if (visible() && stale()) { loadGadgetData(key); } }, every);
    return () => {
      entry.listeners.delete(setValue);
      clearInterval(timer);
    };
  // The loader is read when the key changes; a new function for the same key is the same request.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, every]);

  const refresh = useCallback(() => (key ? loadGadgetData(key) : Promise.resolve(null)), [key]);
  return { data: value.data, error: value.error, loading: value.loading, refresh };
}
